/* eslint-disable react/require-default-props */
// eslint-disable-next-line import/no-unresolved
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import ReactHtmlParser from 'react-html-parser';

import { Icon } from '@powdr/components';
import { ColorProfiles } from '@powdr/constants';
import { AppContext } from '@powdr/context';
import { colorProfileByIndex, componentFactory } from '@powdr/utils';

import {
  StyledCollapsePanel, ExpandButton, PanelBodyWrapper,
  TitleText, CopyText, HeaderLeft, HeaderRight, PanelHeader,
  ComponentWrapper,
  SubheaderWrapper,
  ButtonDetailWrapper,
  AddtionalLabelText,
} from './styles';

export const CollapsePanel = ({
  id,
  cpHeader = null,
  cpBody = null,
  largerTitleText = false,
  panelColorProfile = null,
  parentColorProfile = null,
  relationships = null,
  children = null,
  openPanelOnLoad = false,
  skipAnimation = false,
  scrollToID = null,
}) => {
  const { fullHeaderHeight } = useContext(AppContext);
  const [isPanelOpen, setPanelOpen] = useState(false);
  const panelHeader = cpHeader || relationships?.panelHeader || null;
  const panelBody = cpBody || relationships?.panelBody || null;
  const bodyComponents = panelBody?.relationships?.bodyContent || children || [];
  const trueColorProfile = panelColorProfile
    || (parentColorProfile && Object.values(ColorProfiles).includes(parentColorProfile)
      ? parentColorProfile
      : colorProfileByIndex(parentColorProfile));
  const animationSpeed = (!skipAnimation) ? 500 : 0; // ms
  const adjustPadding = (trueColorProfile === (parentColorProfile || ColorProfiles.BASE));

  useEffect(() => {
    setPanelOpen(openPanelOnLoad);
  }, [openPanelOnLoad]);

  useEffect(() => {
    if (scrollToID) {
      const anchorSection = document.getElementById(scrollToID);
      if (anchorSection) {
        window.scrollTo({
          behavior: 'smooth',
          top: anchorSection.offsetTop - fullHeaderHeight - 150,
        });
      }
    }
  }, [fullHeaderHeight, scrollToID]);

  if (!panelHeader) return null;

  return (
    <StyledCollapsePanel
      key={id}
      id={scrollToID}
      $colorProfile={trueColorProfile}
      $adjustPadding={adjustPadding}
    >
      <PanelHeader
        className="panel-header"
        isContainer={panelHeader?.isContainer}
        onClick={() => setPanelOpen(!isPanelOpen)}
      >
        <HeaderLeft>
          <TitleText
            colorProfile={trueColorProfile}
            $largerText={largerTitleText}
          >
            {panelHeader?.title}
          </TitleText>
          {(panelHeader?.headerCopy) && (
            <CopyText colorProfile={trueColorProfile}>
              {panelHeader?.headerCopy}
            </CopyText>
          )}
          {(panelHeader?.subheader) && (
            <SubheaderWrapper colorProfile={trueColorProfile} $mobile>
              {ReactHtmlParser(panelHeader.subheader)}
            </SubheaderWrapper>
          )}
        </HeaderLeft>
        <HeaderRight $spaceBetween={!!(panelHeader?.subheader)}>
          {(panelHeader?.subheader) && (
            <SubheaderWrapper colorProfile={trueColorProfile} $mobile={false}>
              {ReactHtmlParser(panelHeader.subheader)}
            </SubheaderWrapper>
          )}
          <ButtonDetailWrapper>
            {(panelHeader?.additionalLabel) && (
              <AddtionalLabelText>
                {panelHeader?.additionalLabel}
              </AddtionalLabelText>
            ) }
            <ExpandButton
              colorProfile={trueColorProfile}
              $hasText={(panelHeader?.buttonText)}
              $animationSpeed={animationSpeed}
            >
              {panelHeader?.buttonText}
              <Icon className={classNames('expand-icon', { open: isPanelOpen })} name="ui-chevron-down" width={15} />
            </ExpandButton>
          </ButtonDetailWrapper>
        </HeaderRight>
      </PanelHeader>
      <PanelBodyWrapper>
        <AnimateHeight duration={animationSpeed} height={(isPanelOpen) ? 'auto' : 0}>
          <ComponentWrapper isContainer={panelBody?.isContainer}>
            {(children) && bodyComponents}
            {(relationships) && componentFactory(bodyComponents, trueColorProfile)}
          </ComponentWrapper>
        </AnimateHeight>
      </PanelBodyWrapper>
    </StyledCollapsePanel>
  );
};

CollapsePanel.propTypes = {
  id: PropTypes.string.isRequired,
  cpHeader: PropTypes.shape({}),
  cpBody: PropTypes.shape({}),
  relationships: PropTypes.shape({}),
  panelColorProfile: PropTypes.string,
  parentColorProfile: PropTypes.string,
  children: PropTypes.node,
  largerTitleText: PropTypes.bool,
  openPanelOnLoad: PropTypes.bool,
  skipAnimation: PropTypes.bool,
  scrollToID: PropTypes.string,
};
